import { Link, useNavigate } from 'react-router-dom';

import { AREA_COLORS, Heatmap, LineChart, OTHER_COLOR, Sparkline } from '@/components/charts';
import {
  Card,
  CustomersToggle,
  Empty,
  HBars,
  Kpi,
  Loaded,
  PageHead,
  RangeSeg,
  Seg,
  Select,
  SkeletonCard,
  Toggle,
} from '@/components/ui';
import { useApi, useMeta } from '@/lib/api';
import { cx } from '@/lib/cx';
import { countDelta, fmtDay, fmtInt, fmtMoney, fmtPct, rateDelta } from '@/lib/format';
import { feedHref, RANGES, useCustomers, useParam } from '@/lib/params';
import type { ChartMetric, Meta, Overview } from '@/lib/types';

const METRICS: { value: ChartMetric; label: string }[] = [
  { value: 'asks', label: 'Asks' },
  { value: 'users', label: 'Users' },
  { value: 'not_ok_rate', label: 'Not-ok %' },
  { value: 'cost', label: 'Cost' },
];
const METRIC_TITLE: Record<ChartMetric, string> = {
  asks: 'Asks per day by area',
  users: 'Users per day by area',
  not_ok_rate: 'Not-ok rate per day by area',
  cost: 'Cost per day by area',
};
const METRIC_FORMAT: Record<ChartMetric, (value: number) => string> = {
  asks: fmtInt,
  users: fmtInt,
  not_ok_rate: fmtPct,
  cost: fmtMoney,
};
const RANGE_OPTIONS = [...RANGES.overview, { value: 'custom', label: 'Custom' }];

function Emph({ text, emphasis }: { text: string; emphasis: string | null }) {
  if (!emphasis || !text.includes(emphasis)) return <>{text}</>;
  const at = text.indexOf(emphasis);
  return (
    <>
      {text.slice(0, at)}
      <b>{emphasis}</b>
      {text.slice(at + emphasis.length)}
    </>
  );
}

function OverviewSkeleton() {
  return (
    <>
      <div className="kpis">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="card">
            <div className="skel" style={{ height: 10, width: '60%', marginBottom: 10 }} />
            <div className="skel" style={{ height: 24, width: '40%' }} />
          </div>
        ))}
      </div>
      <div className="row-a">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    </>
  );
}

function Body({ data, meta, compare }: { data: Overview; meta: Meta | undefined; compare: boolean }) {
  const navigate = useNavigate();
  const [range, setRange] = useParam('range', '7d');
  const [metricParam, setMetric] = useParam('metric', 'asks');
  const [customers] = useCustomers();
  const metric = METRICS.find((m) => m.value === metricParam)?.value ?? 'asks';
  const k = data.kpis;
  const shortLabel = (key: string, label: string) => meta?.areas.find((a) => a.key === key)?.short ?? label;
  const vs = (value: number | null, format: (v: number) => string) =>
    compare && value !== null ? `vs ${format(value)}` : undefined;
  const [from] = useParam('from', '');
  const [to] = useParam('to', '');
  const [area] = useParam('area', '');
  const feedRange = { ...(range === 'custom' ? { from, to } : { range }), area };

  if (k.asks.value === 0 && k.active_companies.value === 0) {
    return (
      <div className="card">
        <Empty
          icon="◌"
          title={customers ? 'No activity from customers in this range' : 'No activity in this range'}
          actions={
            range !== '30d' && (
              <button type="button" className="btn" onClick={() => setRange('30d')}>
                Show 30 days
              </button>
            )
          }
        >
          Try 30 days{customers && ', or turn off “Customers only” to include internal and sample companies'}.
        </Empty>
      </div>
    );
  }

  const series = data.chart.areas.map((a, i) => ({
    key: a.key,
    label: a.key === '_other' ? a.label : shortLabel(a.key, a.label),
    color: a.key === '_other' ? OTHER_COLOR : AREA_COLORS[i],
    values: data.chart.metrics[metric][a.key] ?? [],
  }));
  const dayTotal = (i: number) => series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0);

  return (
    <>
      <div className="kpis">
        <Kpi
          label={
            <>
              Active companies
              <span className="faint" title="Companies with an AI ask or a user action in this range">
                ⓘ
              </span>
            </>
          }
          value={fmtInt(k.active_companies.value)}
          delta={compare ? countDelta(k.active_companies.value, k.active_companies.prev) : null}
          compare={vs(k.active_companies.prev, fmtInt)}
          spark={k.active_companies.spark}
          onClick={() => navigate('/accounts')}
        />
        <Kpi
          label="Active users"
          value={fmtInt(k.active_users.value)}
          delta={compare ? countDelta(k.active_users.value, k.active_users.prev) : null}
          compare={vs(k.active_users.prev, fmtInt)}
          spark={k.active_users.spark}
          onClick={() => navigate('/accounts?view=users')}
        />
        <Kpi
          label="AI asks"
          value={fmtInt(k.asks.value)}
          delta={compare ? countDelta(k.asks.value, k.asks.prev) : null}
          compare={vs(k.asks.prev, fmtInt)}
          spark={k.asks.spark}
          onClick={() => navigate(feedHref({ ...feedRange, show: 'asks,mcp', customers }))}
        />
        <Kpi
          label="Not-ok rate"
          value={fmtPct(k.not_ok_rate.value)}
          delta={compare ? rateDelta(k.not_ok_rate.value, k.not_ok_rate.prev) : null}
          compare={vs(k.not_ok_rate.prev, fmtPct)}
          spark={k.not_ok_rate.spark}
          sparkBad
          onClick={() => navigate(feedHref({ ...feedRange, outcome: 'not_ok', show: 'asks,mcp,system', customers }))}
        />
        <Kpi
          label="AI spend"
          value={fmtMoney(k.spend.value)}
          footer={<span className="d muted">{k.spend.per_ask !== null && `${fmtMoney(k.spend.per_ask)} per ask`}</span>}
          spark={k.spend.spark}
          onClick={() => navigate('/cost')}
        />
      </div>

      <div className="row-a">
        <Card
          title={METRIC_TITLE[metric]}
          right={<Seg options={METRICS} value={metric} onChange={(m) => setMetric(m)} compact />}
        >
          <div className="legend" style={{ marginBottom: 8 }}>
            {series.map((s) => (
              <span key={s.key}>
                <i style={{ background: s.color }} />
                {s.label}
              </span>
            ))}
          </div>
          {series.length ? (
            <LineChart
              labels={data.chart.days.map(fmtDay)}
              days={data.chart.days}
              series={series}
              format={METRIC_FORMAT[metric]}
              title={(i) =>
                metric === 'asks'
                  ? `${fmtDay(data.chart.days[i])} · ${fmtInt(dayTotal(i))} asks`
                  : fmtDay(data.chart.days[i])
              }
              onPick={(i) => navigate(feedHref({ from: data.chart.days[i], to: data.chart.days[i], area, customers }))}
            />
          ) : (
            <Empty icon="◌" title="No asks in these days" />
          )}
        </Card>
        <Card
          title="Needs attention"
          right={
            <Link className="link" to="/issues">
              See all →
            </Link>
          }
        >
          {data.attention.length === 0 ? (
            <div className="muted">Nothing needs attention in this range.</div>
          ) : (
            data.attention.map((a) => (
              <div key={`${a.pill}-${a.link}`} className="att" onClick={() => navigate(a.link)}>
                <span className={cx('pill', a.tone)}>{a.pill}</span>
                <div>
                  <div>
                    <Emph text={a.title} emphasis={a.emphasis} />
                  </div>
                  <div className="m">{a.meta}</div>
                </div>
              </div>
            ))
          )}
        </Card>
      </div>

      <div
        className="row-b"
        style={meta?.ai_enabled ? undefined : { gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)' }}
      >
        <Card
          title="Most active companies"
          right={
            <Link className="link" to="/accounts">
              All accounts →
            </Link>
          }
        >
          {data.top_companies.length === 0 ? (
            <div className="muted">No company asked anything in this range.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Trend</th>
                  <th className="num">Asks</th>
                  <th className="num">Users</th>
                  <th className="num">Not-ok</th>
                </tr>
              </thead>
              <tbody>
                {data.top_companies.map((c) => (
                  <tr key={c.company_id} onClick={() => navigate(`/accounts/${c.company_id}`)}>
                    <td>
                      <b>{c.name}</b>
                      <div className="faint" style={{ fontSize: 11 }}>
                        {c.plan ?? '–'}
                      </div>
                    </td>
                    <td>
                      <Sparkline values={c.spark} width={56} height={18} />
                    </td>
                    <td className="num">{fmtInt(c.asks)}</td>
                    <td className="num">{fmtInt(c.users)}</td>
                    <td className="num">
                      <span className={cx('pill', (c.not_ok_rate ?? 0) >= 0.1 ? 'bad' : 'neu')}>
                        {fmtPct(c.not_ok_rate)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
        <Card
          title="Top failure causes"
          right={
            <Link className="link" to="/issues">
              Issues →
            </Link>
          }
        >
          {data.failure_causes.length === 0 ? (
            <div className="muted">No failures in this range.</div>
          ) : (
            <HBars
              bad
              rows={data.failure_causes.map((f) => ({ key: String(f.issue_id), label: f.title, value: f.events }))}
              onClick={(id) => navigate(`/issues/${id}`)}
            />
          )}
          <div className="note">
            Grouped by error pattern.
            {meta?.ai_enabled === false && " “Couldn't answer” isn't detected while AI analysis is off."}
          </div>
        </Card>
      </div>

      <Card
        style={{ marginTop: 12 }}
        title={
          <>
            When people use it
            <span className="muted" style={{ fontWeight: 500 }}>
              {' '}
              · asks by weekday and hour, UTC
            </span>
          </>
        }
        right={
          <span className="legend">
            fewer
            {[0.12, 0.35, 0.6, 0.9].map((a) => (
              <i key={a} style={{ width: 12, height: 12, background: `rgb(var(--heat) / ${a})`, marginRight: 0 }} />
            ))}
            more
          </span>
        }
      >
        <Heatmap grid={data.heatmap} />
      </Card>
    </>
  );
}

export function OverviewPage() {
  const meta = useMeta();
  const [range] = useParam('range', '7d');
  const [from, setFrom] = useParam('from', '');
  const [to, setTo] = useParam('to', '');
  const [area, setArea] = useParam('area', '');
  const [plan, setPlan] = useParam('plan', '');
  const [compare, setCompare] = useParam('compare', 'true');
  const [customers] = useCustomers();
  const custom = range === 'custom';
  const query = useApi<Overview>(
    '/overview',
    custom ? { from, to, area, plan, customers } : { range, area, plan, customers },
    { enabled: !custom || Boolean(from && to) },
  );
  const w = query.data?.window;
  return (
    <div className="page">
      <PageHead
        title="Overview"
        subtitle={
          w
            ? `${fmtDay(w.start)} – ${fmtDay(w.end)}${compare === 'true' ? ` · compared with ${fmtDay(w.prev_start)} – ${fmtDay(w.prev_end)}` : ''}`
            : ' '
        }
      >
        <RangeSeg options={RANGE_OPTIONS} fallback="7d" />
        {custom && (
          <>
            <label className="sel">
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value || null)} />
            </label>
            <label className="sel">
              <input type="date" value={to} onChange={(e) => setTo(e.target.value || null)} />
            </label>
          </>
        )}
        <Select
          value={area}
          active={Boolean(area)}
          onChange={(v) => setArea(v || null)}
          options={[
            { value: '', label: 'All areas' },
            ...(meta.data?.areas ?? []).map((a) => ({ value: a.key, label: a.label })),
          ]}
        />
        <Select
          value={plan}
          active={Boolean(plan)}
          onChange={(v) => setPlan(v || null)}
          options={[
            { value: '', label: 'All plans' },
            ...(meta.data?.plans ?? []).map((p) => ({ value: p, label: p })),
          ]}
        />
        <Toggle on={compare === 'true'} label="Compare" onChange={(on) => setCompare(on ? 'true' : 'false')} />
        <CustomersToggle />
      </PageHead>
      {custom && !(from && to) ? (
        <div className="card">
          <Empty icon="📅" title="Pick a start and end date" />
        </div>
      ) : (
        <Loaded query={query} skeleton={<OverviewSkeleton />}>
          {(data) => <Body data={data} meta={meta.data} compare={compare === 'true'} />}
        </Loaded>
      )}
    </div>
  );
}
